// WidgetPreviewPanel — hand-built Preview override-slot for the Widget Studio
// instance detail (CHG-2473 / ITER-905216, Widget Studio S6).
//
// Injected into the generated widget-instance-detail.ts `tab-preview`
// override-slot (override_slot: true, slot_component: WidgetPreviewPanel in the
// entity-hint). Fetches GET /admin/widgets/instances/{id}/preview and renders
// the admin preview output SANDBOXED: when render is allowed, the resolved
// render-data (config + content) is shown inside a fully-restricted iframe
// (sandbox="" — no scripts, no same-origin, no top-navigation) so untrusted
// widget content can never execute or escape the dashboard. When render is not
// allowed, the BE reason is surfaced instead.

import { useQuery } from "@tanstack/react-query";
import { widgetsAdminApi } from "../../api/widgets-admin";
import type { WidgetRenderDataResponse } from "../../api/widgets-admin";
import { useFormatters } from "../../contexts/IntlContext";

function escapeHtml(value: unknown): string {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

// Build a static, fully-escaped HTML document from the resolved render-data.
// Everything is escaped, and the iframe is sandboxed with NO allow-scripts, so
// this is display-only and inert by construction.
function buildPreviewHtml(rd: WidgetRenderDataResponse): string {
  const items = Array.isArray(rd.content) ? rd.content : [];
  const cards = items
    .map((it) => {
      const rec = it as unknown as Record<string, unknown>;
      const title = escapeHtml(rec.title ?? rec.id ?? "Item");
      const lines = Object.entries(rec)
        .filter(([k]) => k !== "title")
        .map(
          ([k, v]) =>
            `<div class="row"><span class="k">${escapeHtml(k)}</span><span class="v">${escapeHtml(v)}</span></div>`,
        )
        .join("");
      return `<div class="card"><div class="title">${title}</div>${lines}</div>`;
    })
    .join("");
  const empty = items.length
    ? ""
    : `<div class="muted">No content resolved for this widget.</div>`;
  return `<!doctype html><html><head><meta charset="utf-8"><style>
    :root{color-scheme:light}
    body{font:13px/1.5 system-ui,-apple-system,Segoe UI,Roboto,sans-serif;margin:0;padding:16px;color:#1f2937;background:#fff}
    .meta{font-size:12px;color:#6b7280;margin-bottom:12px}
    .card{border:1px solid #e5e7eb;border-radius:8px;padding:12px;margin-bottom:10px}
    .title{font-weight:600;margin-bottom:6px}
    .row{display:flex;justify-content:space-between;gap:12px;font-size:12px;padding:2px 0}
    .k{color:#6b7280}.v{text-align:right;word-break:break-word}
    .muted{color:#9ca3af}
  </style></head><body>
    <div class="meta">family: ${escapeHtml(rd.widget_family)} · currency: ${escapeHtml(rd.currency)} · tz: ${escapeHtml(rd.org_timezone)}</div>
    ${cards}${empty}
  </body></html>`;
}

export function WidgetPreviewPanel({ instanceId }: { instanceId: string }) {
  const fmt = useFormatters();
  const { data, isLoading, isError } = useQuery({
    queryKey: ["widget-instances", "preview", instanceId],
    queryFn: () => widgetsAdminApi.preview(instanceId),
  });

  if (isLoading) {
    return (
      <div className="card" style={{ padding: 16, color: "var(--color-text-muted)" }}>
        Loading preview…
      </div>
    );
  }
  if (isError || !data) {
    return (
      <div className="card" style={{ padding: 16, color: "var(--color-error)" }}>
        Could not load the preview.
      </div>
    );
  }

  if (!data.render_allowed || !data.render_data) {
    return (
      <div className="card" style={{ padding: 16 }}>
        <div style={{ fontWeight: 600, marginBottom: 6 }}>Preview not available</div>
        <div style={{ color: "var(--color-text-muted)", fontSize: 13 }}>
          {data.reason ?? "This widget cannot be previewed in its current state."}
        </div>
      </div>
    );
  }

  const rd = data.render_data;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ fontSize: 12, color: "var(--color-text-muted)" }}>
        Sandboxed admin preview · generated {fmt.formatDate(rd.generated_at, "relative")}
      </div>
      <iframe
        title="Widget preview"
        sandbox=""
        srcDoc={buildPreviewHtml(rd)}
        style={{
          width: "100%",
          minHeight: 360,
          border: "1px solid var(--color-border-subtle)",
          borderRadius: 8,
          background: "var(--color-bg-elevated, #fff)",
        }}
      />
    </div>
  );
}
